import React, { useRef, useEffect, useState, useImperativeHandle } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Tooltip,
  Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { TimePicker } from "@material-ui/pickers";
import * as dayjs from "dayjs";
import { useRequest } from "ahooks";
import { CompanyDeviceNamingDatatable } from "@/helpers/CompanyDeviceNamingDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import {
  getCompanySettingInfo,
  updateCompanyOraAutoUpdate,
  updateCompanySettingInfo,
  updateNamingStatus
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import { useGlobalState } from "@/GlobalProvider";
import { IconTitleDot } from "@/components/Icon";
import { HelpOutline } from "@material-ui/icons";
import HasPermission from "@/components/commons/HasPermission";
import Modal from "@/components/Modal";

const useStyles = makeStyles(theme => ({
  main: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,

    "& .MuiPaper-root": {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column"
    }
  },
  content: {
    flex: 1,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
    paddingLeft: theme.spacing(3.5),
    paddingRight: theme.spacing(3.5)
  },
  title: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  required: {
    "&::after": {
      content: "'*'",
      fontSize: "1.25rem",
      verticalAlign: -3,
      fontFamily: "Arial",
      color: theme.palette.error.main
    }
  },
  dot: {
    fontSize: "1.25rem",
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(-3.2)
  },
  desc: {
    marginBottom: theme.spacing(2)
  },
  rangeTimePicker: {
    display: "flex",
    marginBottom: theme.spacing(2.5)
  },
  rangeDelimiter: {
    margin: `0 ${theme.spacing(2)}px`,
    alignSelf: "center",
    color: theme.palette.text.secondary
  }
}));

function timeToString(time) {
  return dayjs(time).format("HH:mm:ss");
}

const CompanySettingMain = React.forwardRef((props, ref) => {
  const { t, langConf } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const [updateStart, setUpdateStart] = useState(null);
  const [updateEnd, setUpdateEnd] = useState(null);
  const [autoUpdate, setAutoUpdate] = useState(0);
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [timeConfig, setTimeConfig] = useState({
    updatedAt: "",
    interval: 300
  })

  const history = useHistory();
  const { currentCompanyId } = useGlobalState();

  const reloadTableWithGroupId = () => {
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReloadDataTable(true);
  };
  const doEnable = async (rows, enable) => {
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    await updateNamingStatus({
      namingRuleIds: rows.map(row => row.id),
      status: enable ? 0 : 1
    });
    handleReloadDataTable();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  const handleEnable = (row, enable) => {
    modalConfirm({
      title: enable
        ? t({ defaultMessage: "启用命名规则" })
        : t({ defaultMessage: "禁用命名规则" }),
      content: enable
        ? t(
            {
              defaultMessage: "是否确认启用（{name}）该命名规则？"
            },
            { name: row.name }
          )
        : t(
            {
              defaultMessage: "是否确认禁用（{name}）该命名规则？"
            },
            { name: row.name }
          ),
      onConfirm: () => {
        return doEnable(row, enable);
      }
    });
  };

  const handleEdit = row => {
    history.push("/setting/device-naming/edit/" + row.id);
  };

  const handleView = row => {
    history.push("/setting/device-naming/info/" + row.id);
  };

  const handleUpdate = (time, type) => {
    if (type === "start") {
      setUpdateStart(time);
      if (timeToString(updateEnd) === "Invalid Date") return;
      if (timeToString(time) === "Invalid Date") return;
      // updateCompanySettingInfo({
      //   updateStart: timeToString(time),
      //   updateEnd: timeToString(updateEnd)
      // });
    }
    if (type === "end") {
      setUpdateEnd(time);
      if (timeToString(updateStart) === "Invalid Date") return;
      if (timeToString(time) === "Invalid Date") return;
      // updateCompanySettingInfo({
      //   updateStart: timeToString(updateStart),
      //   updateEnd: timeToString(time)
      // });
    }
  };

  const handleAutoUpdateChange = e => {
    setAutoUpdate(e.target.checked ? 1 : 0);
    // updateCompanyOraAutoUpdate({ oraAutoUpd: e.target.checked ? 1 : 0 });
  };

  const { refresh: refreshCompanySettingInfo } = useRequest(async () => {
    const info = await getCompanySettingInfo();
    setUpdateStart(dayjs(dayjs().format("YYYY-MM-DD ") + info.updateStart));
    setUpdateEnd(dayjs(dayjs().format("YYYY-MM-DD ") + info.updateEnd));
    setAutoUpdate(info.oraAutoUpd);
    setTimeConfig({
      updatedAt: info.configLastUpdatedAt,
      interval: info.configUpdateTimeInterval,
    })

    return info;
  });

  useImperativeHandle(ref, () => ({
    save: () => setSaveModalOpen(true)
  }))

  const save = async () => {
      await updateCompanySettingInfo({
        oraAutoUpd: autoUpdate,
        updateStart: timeToString(updateStart),
        updateEnd: timeToString(updateEnd)
      })
    }

  useEffect(() => {
    resetQuery();
    refreshCompanySettingInfo();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <div className={classes.content}>
          <div>
            <Box className={classes.title}>
              <IconTitleDot className={classes.dot} />
              <Typography variant="body1">
                {t({ defaultMessage: "1. {ora}自动更新" }, { ora: langConf.deviceCenter?.oraText || "Ora" })}
              </Typography>
              <Tooltip
                title={t({
                  defaultMessage:
                    "关闭自动更新后，设备上{ora}版本差异可能会导致部分功能无法使用"
                }, { ora: langConf.deviceCenter?.oraText })}
              >
                <HelpOutline
                  style={{ color: "gray", fontSize: 14, marginLeft: 6 }}
                />
              </Tooltip>
              <Switch
                checked={autoUpdate === 1}
                color="primary"
                onChange={handleAutoUpdateChange}
                size="small"
              />
            </Box>
            <Typography
              variant="subtitle"
              color="textSecondary"
              component="div"
            >
              {t({
                defaultMessage: "开启后，若{ora}有新版本时将自动下载与更新。"
              }, { ora: langConf.deviceCenter?.oraText || "Ora"})}
            </Typography>
            <Typography
              variant="subtitle"
              color="textSecondary"
              component="div"
              style={{ marginBottom: 24, marginTop: 8 }}
              // className={classes.desc}
            >
              {t({
                defaultMessage:
                  "关闭后，您可自行下载新版{ora}安装，或是透过设备命令-{ora}更新进行手动更新。"
              }, { ora: langConf.deviceCenter?.oraText || "Ora" })}
            </Typography>
          </div>
          <Box className={classes.title}>
            <IconTitleDot className={classes.dot} />
            <Typography variant="body1" className={classes.required}>
              {t({ defaultMessage: "2. 设备可更新时间段（当地时间）" })}
            </Typography>
          </Box>
          <div className={classes.rangeTimePicker}>
            <TimePicker
              ampm={false}
              value={updateStart}
              onChange={value => handleUpdate(value, "start")}
              renderInput={props => (
                <TextField
                  {...props}
                  helperText=""
                  inputProps={{
                    ...props.inputProps,
                    placeholder: t({ defaultMessage: "开始时间" })
                  }}
                />
              )}
            />
            <span className={classes.rangeDelimiter}>-</span>
            <TimePicker
              ampm={false}
              value={updateEnd}
              onChange={value => handleUpdate(value, "end")}
              renderInput={props => (
                <TextField
                  {...props}
                  helperText=""
                  inputProps={{
                    ...props.inputProps,
                    placeholder: t({ defaultMessage: "结束时间" })
                  }}
                />
              )}
            />
          </div>
        </div>
      </Paper>
      <Modal
        title={t({ defaultMessage: "变更提示" })}
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onConfirm={async () => {
          if (timeConfig.updatedAt && (Number(timeConfig.updatedAt)+(timeConfig.interval*1000)) < Date.now())  {
            await save()
            refreshCompanySettingInfo();
          }
          setSaveModalOpen(false)
        }}
      >
        { timeConfig.updatedAt && (Number(timeConfig.updatedAt)+(timeConfig.interval*1000)) > Date.now()?(
          <Typography>
            {t({ defaultMessage: "下一次可变更时间{time}" }, {time: dayjs(Number(timeConfig.updatedAt)+(timeConfig.interval*1000)).format("YYYY/MM/DD HH:mm:ss")})}
          </Typography>
        ):(
          <Typography>
            {t({ defaultMessage: "确认后设备将套用新的配置,且一天内无法再变更设定,确定要套用吗?" })}
          </Typography>
        )}
      </Modal>
    </div>
  );
});

export default CompanySettingMain;
